import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App.jsx";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store/store";
import ErrorBoundary from "./errorBoundary";
import { Toaster } from "react-hot-toast";

// Enhanced error handling with better debugging
const originalConsoleError = console.error;
console.error = (...args) => {
  const errorMessage = args[0]?.toString?.() || '';
  const stackTrace = args[1]?.toString?.() || '';
  
  // Only suppress known harmless ResizeObserver errors
  if (
    errorMessage.includes('ResizeObserver loop limit exceeded') ||
    errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
    (args[0]?.name === 'ResizeObserver loop limit exceeded')
  ) {
    // Log suppressed errors in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.warn('Suppressed ResizeObserver error:', errorMessage);
    }
    return;
  }
  
  // Log other errors normally
  originalConsoleError.apply(console, args);
};

// Enhanced window error handler
const originalWindowError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  const errorMessage = message?.toString?.() || '';
  const errorSource = source?.toString?.() || '';
  
  // Only suppress specific ResizeObserver loop errors
  if (
    errorMessage.includes('ResizeObserver loop limit exceeded') ||
    error?.name === 'ResizeObserver loop limit exceeded'
  ) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Suppressed window ResizeObserver error:', errorMessage);
    }
    return true;
  }
  
  // Log other errors normally
  if (originalWindowError) {
    return originalWindowError(message, source, lineno, colno, error);
  }
  return false;
};

// Enhanced unhandled promise rejection handler
const originalUnhandledRejection = window.onunhandledrejection;
window.onunhandledrejection = (event) => {
  const reason = event.reason?.toString?.() || '';
  
  // Only suppress specific ResizeObserver promise rejections
  if (reason.includes('ResizeObserver loop limit exceeded')) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Suppressed ResizeObserver promise rejection:', reason);
    }
    event.preventDefault();
    return;
  }
  
  // Log other rejections normally
  if (originalUnhandledRejection) {
    return originalUnhandledRejection(event);
  }
};

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ErrorBoundary>
          <App />
          <Toaster />
        </ErrorBoundary>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);

reportWebVitals();
